<script setup lang="ts">
import { useI18n } from '@/i18n';
import { Button } from '@dabble/quillui';
import { onMounted, onUnmounted } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';

/**
 * Full-screen focus layout for the checkout flow (plan selection + payment).
 * Deliberately drops the account sidebar so the buyer's attention stays on one
 * decision at a time. Provides a slim top bar (wordmark + slotted actions + a
 * close affordance) and an optional footer back link.
 */
const props = defineProps<{
  /** Footer back link target. Omit to hide the back link. */
  backTo?: RouteLocationRaw;
  backLabel?: string;
}>();

const { t } = useI18n();
const router = useRouter();

/** Close always exits the flow back to the billing overview. */
function close() {
  router.push({ name: 'portal' });
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close();
}

onMounted(() => document.addEventListener('keydown', onKeydown));
onUnmounted(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div class="checkout-frame">
    <header class="checkout-frame__bar">
      <RouterLink :to="{ name: 'portal' }" class="checkout-frame__wordmark" :aria-label="t('checkout_frame_home')">
        <img src="/images/dabble-logo-full.png" alt="" class="checkout-frame__logo" />
      </RouterLink>
      <div class="checkout-frame__actions">
        <slot name="actions" />
        <Button
          icon-only="close"
          variant="secondary"
          finish="ghost"
          round
          :aria-label="t('checkout_frame_close')"
          @click="close"
        />
      </div>
    </header>

    <main class="checkout-frame__content">
      <div class="checkout-frame__inner">
        <slot />
        <div v-if="props.backTo" class="checkout-frame__footer">
          <Button :to="props.backTo" variant="secondary" finish="link" icon-left="arrow-left">
            {{ props.backLabel ?? t('billing_go_back') }}
          </Button>
        </div>
      </div>
    </main>
  </div>
</template>

<style lang="scss" scoped>
.checkout-frame {
  position: fixed;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  background-color: theme('color-bg-app');
  overflow: auto;

  &__bar {
    position: sticky;
    top: 0;
    z-index: 1;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: space('small');
    padding: space('small') space('medium');
    background-color: theme('color-bg-app');
    border-bottom: 1px solid theme('color-border-secondary');
  }

  &__wordmark {
    display: inline-flex;
    align-items: center;
    text-decoration: none;

    &:focus-visible {
      outline: 2px solid theme('color-fg-primary-interactive');
      outline-offset: 3px;
      border-radius: borderRadius('tiny');
    }
  }

  &__logo {
    display: block;
    height: 1.75rem;
    width: auto;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: space('small');
  }

  &__content {
    flex: 1;
    display: flex;
    justify-content: center;
    padding: space('xlarge') space('medium') space('xxlarge');
  }

  &__inner {
    width: 100%;
    max-width: 66rem;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__footer {
    margin-top: space('xlarge');
    display: flex;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .checkout-frame__content {
    padding: space('large') space('small') space('xlarge');
  }
}
</style>
